import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useConnectivity } from '../hooks/useConnectivity';
import { useTranslation } from '../hooks/useTranslation';
import { NativeBridge } from '../native/NativeBridge';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { speakOrBeep } from '@caro/shared/utils';
import type { EmergencyStatus } from '@caro/shared/types';
import type { Language } from '../i18n/types';

const DEFAULT_COUNTDOWN = 10;
const BEEP_FREQUENCY = 800;
const BEEP_DURATION = 150;

/**
 * Auto-cancel window on the emergency confirmation screen.
 *
 * Spec D3 (resident-app-elderly-ux): extended from 10s → 20s so an
 * elderly user who pressed the emergency button by accident has enough
 * time to react. At {@link CONFIRM_TTS_WARNING_AT_MS} (5s before the
 * window expires), a localized "5 seconds remaining" TTS cue fires with
 * a Web Audio beep fallback. The cue is hard-coded on per spec — no
 * user-facing toggle, since it is a safety-critical heads-up.
 */
const CONFIRM_AUTOCANCEL_MS = 20_000;
const CONFIRM_TTS_WARNING_AT_MS = 15_000;

/** Map app language codes to BCP-47 tags for the TTS utterance. */
const SPEECH_LANG_MAP: Record<Language, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  fr: 'fr-FR',
  de: 'de-DE',
};

/** Play an audio beep using Web Audio API */
function playBeep(frequency = BEEP_FREQUENCY, duration = BEEP_DURATION) {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = 'square';
    gain.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      ctx.close();
    }, duration);
  } catch {
    // Audio not available
  }
}

export function EmergencyPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { layers } = useConnectivity();
  const { t, lang } = useTranslation();
  const [status, setStatus] = useState<EmergencyStatus>('confirming');
  const [countdown, setCountdown] = useState(
    user?.emergencyCountdownSeconds || DEFAULT_COUNTDOWN
  );
  const [error, setError] = useState<string | null>(null);
  const emergencyIdRef = useRef<number | null>(null);

  // Hide status bar on mount, show on unmount
  useEffect(() => {
    NativeBridge.hideStatusBar();
    return () => { NativeBridge.showStatusBar(); };
  }, []);

  // Heavy haptic on confirmation
  const handleConfirm = useCallback(() => {
    NativeBridge.hapticImpact('heavy');
    playBeep(600, 200);
    setStatus('counting_down');
  }, []);

  const handleCancel = useCallback(() => {
    setStatus('cancelled');
    NativeBridge.hapticImpact('light');
    setTimeout(() => navigate('/'), 1000);
  }, [navigate]);

  const triggerEmergency = useCallback(async () => {
    setStatus('calling');
    NativeBridge.hapticImpact('heavy');

    // Try API call first
    try {
      const result = await api.post<{ success: boolean; emergencyId: number }>(
        API_ENDPOINTS.EMERGENCY_TRIGGER,
        {
          type: 'manual',
          timestamp: new Date().toISOString(),
        },
      );
      emergencyIdRef.current = result.emergencyId;
      setStatus('connected');
    } catch {
      // API failed — check if SIP is available (Sprint 3)
      // For now, fall back to native dialer
      setError(t.emergency.errorApiFailed);
      setStatus('idle');
    }
  }, [api]);

  const handleReset = useCallback(async () => {
    if (emergencyIdRef.current) {
      try {
        await api.post(API_ENDPOINTS.EMERGENCY_RESET, {
          emergencyId: emergencyIdRef.current,
          resolution: 'handled',
        });
      } catch {
        // Reset may fail, navigate home anyway
      }
    }
    setStatus('resolved');
    navigate('/');
  }, [api, navigate]);

  // Countdown timer with beep + vibrate per tick
  useEffect(() => {
    if (status !== 'counting_down') return;
    if (countdown <= 0) {
      triggerEmergency();
      return;
    }

    // Beep and vibrate on each tick
    playBeep(BEEP_FREQUENCY + (DEFAULT_COUNTDOWN - countdown) * 40, BEEP_DURATION);
    NativeBridge.vibrate();

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, triggerEmergency]);

  // Auto-cancel confirmation after 20 seconds, with a localized TTS
  // warning ("5 seconds remaining") at t=15s (D3 + F3-min). Both timers
  // are scheduled — and cleared — together so navigating away from the
  // confirmation screen never produces a ghost TTS announcement.
  useEffect(() => {
    if (status !== 'confirming') return;
    const autoCancelTimer = setTimeout(() => {
      setStatus('cancelled');
      navigate('/');
    }, CONFIRM_AUTOCANCEL_MS);
    const ttsWarningTimer = setTimeout(() => {
      // Safety-critical cue: hard-coded on, no user setting.
      // `speakOrBeep` never throws — it returns 'spoken' | 'beeped' | 'failed'.
      void speakOrBeep(t.emergency.fiveSecondsRemaining, {
        lang: SPEECH_LANG_MAP[lang] || 'en-US',
      });
    }, CONFIRM_TTS_WARNING_AT_MS);
    return () => {
      clearTimeout(autoCancelTimer);
      clearTimeout(ttsWarningTimer);
    };
  }, [status, navigate, t, lang]);

  // --- CONFIRMING ---
  if (status === 'confirming') {
    return (
      <FullScreenEmergency>
        <h1 style={{ fontSize: 'var(--font-heading)', marginBottom: '24px', textAlign: 'center' }}>
          {t.emergency.confirmationTitle}
        </h1>
        <div style={{ display: 'flex', gap: '24px', width: '100%', maxWidth: '400px' }}>
          {/* T6.2 / D2: Yes/No buttons gain icons in addition to red/green
           *  so red-green color-blind users (~8% of men) can still tell them
           *  apart. Icons are `aria-hidden` because the text label + button
           *  aria-label already convey meaning to screen readers. Icons sit
           *  ABOVE the text in a flex column so they scale gracefully when
           *  the user picks Large / X-Large font size (T2.3). */}
          <button
            onClick={handleConfirm}
            style={{
              flex: 1,
              minHeight: 'var(--touch-emergency)',
              backgroundColor: 'var(--caro-emergency)',
              color: 'white',
              fontSize: 'var(--font-body)',
              fontWeight: 700,
              border: '3px solid white',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '12px',
            }}
            aria-label="Yes, I need help"
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                focusable="false"
              >
                <polyline points="4 12 10 18 20 6" />
              </svg>
            </span>
            <span>{t.emergency.buttonYes}</span>
          </button>
          <button
            onClick={handleCancel}
            style={{
              flex: 1,
              minHeight: 'var(--touch-emergency)',
              backgroundColor: 'var(--caro-success)',
              color: 'white',
              fontSize: 'var(--font-body)',
              fontWeight: 700,
              border: '3px solid white',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '12px',
            }}
            aria-label={t.emergency.buttonNoCancel}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                focusable="false"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </span>
            <span>{t.emergency.buttonNo}</span>
          </button>
        </div>
      </FullScreenEmergency>
    );
  }

  // --- COUNTING DOWN ---
  if (status === 'counting_down') {
    return (
      <FullScreenEmergency>
        <div style={{
          fontSize: 'var(--font-heading)',
          fontWeight: 700,
          marginBottom: '16px',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}>
          {countdown}
        </div>
        <p style={{ fontSize: 'var(--font-body)', marginBottom: '32px', textAlign: 'center' }}>
          {t.emergency.countdownMessage.replace('{seconds}', String(countdown))}
        </p>
        <button
          onClick={handleCancel}
          style={{
            minHeight: 'var(--touch-emergency)',
            minWidth: '200px',
            backgroundColor: 'white',
            color: 'var(--caro-emergency)',
            fontSize: 'var(--font-body)',
            fontWeight: 700,
            border: 'none',
            borderRadius: '12px',
          }}
          aria-label="Cancel emergency"
        >
          {t.emergency.buttonCancel}
        </button>
      </FullScreenEmergency>
    );
  }

  // --- CALLING / CONNECTED ---
  if (status === 'calling' || status === 'connected') {
    return (
      <FullScreenEmergency>
        <h1 style={{ fontSize: 'var(--font-heading)', marginBottom: '16px' }}>
          {status === 'calling' ? t.emergency.calling : t.emergency.connected}
        </h1>
        {status === 'calling' && <Spinner />}
        {status === 'connected' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px', width: '100%', maxWidth: '300px' }}>
            <button
              onClick={handleReset}
              style={{
                minHeight: 'var(--touch-emergency)',
                backgroundColor: 'var(--caro-success)',
                color: 'white',
                fontSize: 'var(--font-body)',
                fontWeight: 700,
                border: 'none',
                borderRadius: '12px',
              }}
            >
              {t.emergency.resetAlarm}
            </button>
            <button
              onClick={() => navigate('/')}
              style={{
                minHeight: 'var(--touch-min)',
                backgroundColor: 'transparent',
                color: 'white',
                fontSize: 'var(--font-body)',
                border: '2px solid white',
                borderRadius: '12px',
              }}
            >
              {t.common.returnHome}
            </button>
          </div>
        )}
      </FullScreenEmergency>
    );
  }

  // --- IDLE (API failed — offer native dialer) ---
  if (status === 'idle' && error) {
    return (
      <FullScreenEmergency>
        <h1 style={{ fontSize: 'var(--font-heading)', marginBottom: '16px', textAlign: 'center' }}>
          {error}
        </h1>
        {!layers.sip && (
          <p style={{ fontSize: 'var(--font-body)', marginBottom: '24px', textAlign: 'center', opacity: 0.9 }}>
            {t.emergency.errorSipUnavailable}
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '300px' }}>
          <button
            onClick={() => NativeBridge.openDialer('112')}
            style={{
              minHeight: 'var(--touch-emergency)',
              backgroundColor: 'white',
              color: 'var(--caro-emergency)',
              fontSize: 'var(--font-body)',
              fontWeight: 700,
              border: 'none',
              borderRadius: '12px',
            }}
          >
            {t.emergency.call112}
          </button>
          <button
            onClick={() => {
              setError(null);
              setCountdown(user?.emergencyCountdownSeconds || DEFAULT_COUNTDOWN);
              setStatus('confirming');
            }}
            style={{
              minHeight: 'var(--touch-min)',
              backgroundColor: 'transparent',
              color: 'white',
              fontSize: 'var(--font-body)',
              border: '2px solid white',
              borderRadius: '12px',
            }}
          >
            {t.emergency.tryAgain}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              minHeight: 'var(--touch-min)',
              backgroundColor: 'transparent',
              color: 'white',
              fontSize: 'var(--font-body)',
              border: 'none',
              textDecoration: 'underline',
            }}
          >
            {t.common.returnHome}
          </button>
        </div>
      </FullScreenEmergency>
    );
  }

  // --- CANCELLED ---
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontSize: 'var(--font-heading)',
      color: 'var(--caro-text-light)',
    }}>
      {t.emergency.cancelled}
    </div>
  );
}

function FullScreenEmergency({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: 'var(--caro-emergency)',
      color: 'white',
      padding: '24px',
    }}>
      {children}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 1; } 100% { transform: scale(1.4); opacity: 0; } }
      `}</style>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ position: 'relative', width: '80px', height: '80px' }}>
      <div style={{
        position: 'absolute',
        width: '80px',
        height: '80px',
        border: '4px solid rgba(255,255,255,0.2)',
        borderTop: '4px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <div style={{
        position: 'absolute',
        width: '80px',
        height: '80px',
        border: '4px solid rgba(255,255,255,0.3)',
        borderRadius: '50%',
        animation: 'pulse-ring 1.5s ease-out infinite',
      }} />
    </div>
  );
}
